import { useEffect, useState } from "react";
import { Fingerprint, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { canOfferBiometricUnlock, registerPasskey } from "@/lib/passkeys";

export function BiometricNudge({ user }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role !== "field") return;
    if (!canOfferBiometricUnlock()) return;
    if (localStorage.getItem("fl_passkey") === "1") return;
    if (localStorage.getItem("fl_bio_nudge") === "done") return;
    if (sessionStorage.getItem("fl_fresh_login") !== "1") return;
    const t = setTimeout(() => {
      sessionStorage.removeItem("fl_fresh_login");
      setShow(true);
    }, 1200);
    return () => clearTimeout(t);
  }, [user]);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem("fl_bio_nudge", "done");
    setShow(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      await registerPasskey();
      localStorage.setItem("fl_bio_nudge", "done");
      toast.success("Sorted — next time just use your fingerprint or face");
      setShow(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "";
      if (!/cancel|abort|not allowed/i.test(String(msg))) {
        toast.error(String(msg) || "Couldn't set that up — you can turn it on later in Me");
      }
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] p-4 pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-6 pointer-events-none" data-testid="biometric-nudge">
      <div className="pointer-events-auto max-w-md mx-auto bg-[#101620] text-white border border-white/15 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-5 animate-in slide-in-from-bottom-4 duration-300">
        <div className="flex items-start gap-4">
          <span className="w-12 h-12 rounded-full border border-[#FF5F15]/60 bg-[#FF5F15]/10 flex items-center justify-center shrink-0">
            <Fingerprint className="w-6 h-6 text-[#FF5F15]" />
          </span>
          <div className="flex-1">
            <div className="font-display font-black uppercase tracking-tight text-base leading-snug">
              Skip the password next time?
            </div>
            <p className="text-sm text-zinc-400 mt-1 leading-snug">
              Turn on fingerprint / face unlock and sign in with one tap.
            </p>
          </div>
          <button onClick={dismiss} className="text-zinc-500 hover:text-white transition-colors -mt-1 -mr-1" data-testid="biometric-nudge-dismiss" aria-label="Not now">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-4 flex gap-2.5">
          <Button
            onClick={enable}
            disabled={busy}
            className="flex-1 h-11 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
            data-testid="biometric-nudge-enable"
          >
            <Fingerprint className="w-4 h-4 mr-1.5" />
            {busy ? "Waiting…" : "Turn it on"}
          </Button>
          <Button
            onClick={dismiss}
            variant="outline"
            className="h-11 rounded-lg bg-transparent border border-white/25 text-zinc-300 hover:bg-white/10 hover:text-white uppercase tracking-wider font-bold px-5"
            data-testid="biometric-nudge-later"
          >
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}
